/**
 * CSV/Excel Upload Wizard
 *
 * Multi-step wizard for uploading and mapping CSV/Excel files to internal fields.
 */

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Loader2,
  Info,
  AlertTriangle
} from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { getApiBaseUrl } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface FieldMapping {
  externalField: string;
  internalField: string;
  confidence: number;
  reasoning: string;
  isManual?: boolean;
}

interface UploadResponse {
  upload_id: number;
  filename: string;
  file_size: number;
  preview: {
    headers: string[];
    rows: any[][];
    total_rows_previewed: number;
  };
  schema: {
    columns: Record<string, any>;
  };
  suggested_mappings: Array<{
    external_field: string;
    suggested_internal_field: string;
    confidence_score: number;
    reasoning: string;
  }>;
}

const steps = ['Upload File', 'Review Data', 'Map Fields', 'Reconciliation Preview', 'Confirm & Import'];

/** Get auth headers for API calls — mirrors apiClient interceptor */
const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('auth_token') || localStorage.getItem('member_token');
  const cboId = localStorage.getItem('active_cbo_id');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (cboId) headers['X-CBO-ID'] = cboId;
  return headers;
};

export const CSVUploadWizard: React.FC = () => {
  const { t } = useAppTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [sourceSystem, setSourceSystem] = useState<string>('');
  const [reconciliationResult, setReconciliationResult] = useState<any>(null);
  const [reconLoading, setReconLoading] = useState(false);

  // Step 1: File Upload
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploadedFile(file);
    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Build URL with query parameters
      const url = new URL('/api/integrations/csv-excel/upload', window.location.origin);
      if (sourceSystem) {
        url.searchParams.append('source_system', sourceSystem);
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data: UploadResponse = await response.json();
      setUploadResponse(data);

      // Initialize field mappings from suggestions
      const mappings: FieldMapping[] = Array.isArray(data.suggested_mappings)
        ? data.suggested_mappings.map(s => ({
            externalField: s.external_field,
            internalField: s.suggested_internal_field,
            confidence: s.confidence_score,
            reasoning: s.reasoning,
            isManual: false
          }))
        : [];
      setFieldMappings(mappings);

      setActiveStep(1); // Move to next step
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [sourceSystem]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  // Step 2: Review Data (automatic, just display)
  const renderDataPreview = () => {
    if (!uploadResponse) return null;

    return (
      <div>
        <h2 className="text-xl font-semibold mb-4">
          {t('integrations.file_preview', 'File Preview')}
        </h2>
        <Alert className="mb-4 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-900 dark:text-blue-100">
            Showing first {uploadResponse.preview.total_rows_previewed} rows of {uploadResponse.filename}
          </AlertDescription>
        </Alert>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-6">
          <div className="overflow-auto max-h-96">
            <Table>
              <TableHeader className="sticky top-0 bg-white dark:bg-gray-800">
                <TableRow>
                  {uploadResponse.preview.headers.map((header, idx) => (
                    <TableHead key={idx} className="font-bold">
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploadResponse.preview.rows.map((row, rowIdx) => (
                  <TableRow key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      <TableCell key={cellIdx}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setActiveStep(0)}>
            {t('integrations.back', 'Back')}
          </Button>
          <Button variant="primary" onClick={() => setActiveStep(2)}>
            {t('integrations.continue_to_field_mapping', 'Continue to Field Mapping')}
          </Button>
        </div>
      </div>
    );
  };

  // Step 3: Field Mapping
  const handleMappingChange = (externalField: string, newInternalField: string) => {
    setFieldMappings(prev =>
      prev.map(m =>
        m.externalField === externalField
          ? { ...m, internalField: newInternalField, isManual: true, confidence: 1.0 }
          : m
      )
    );
  };

  const renderFieldMapping = () => {
    if (!uploadResponse) return null;

    const totalConfidence = fieldMappings.reduce((sum, m) => sum + m.confidence, 0);
    const avgConfidence = totalConfidence / fieldMappings.length;

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            {t('integrations.field_mapping', 'Field Mapping')}
          </h2>
          <Badge
            variant={avgConfidence >= 0.8 ? 'success' : avgConfidence >= 0.6 ? 'warning' : 'danger'}
            size="md"
            className="gap-1"
          >
            <Sparkles className="h-3.5 w-3.5" />
            AI Confidence: {Math.round(avgConfidence * 100)}%
          </Badge>
        </div>

        <Alert className="mb-4 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-900 dark:text-blue-100">
            Review and adjust the field mappings below. Pre-configured mappings (shown with a checkmark) come from your admin settings.
            Other mappings are AI-suggested based on field name similarity.
          </AlertDescription>
        </Alert>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead><strong>{t('integrations.external_field_csv', 'External Field (CSV)')}</strong></TableHead>
                <TableHead><strong>{t('integrations.maps_to', 'Maps To')}</strong></TableHead>
                <TableHead><strong>{t('integrations.internal_field', 'Internal Field')}</strong></TableHead>
                <TableHead><strong>{t('integrations.confidence', 'Confidence')}</strong></TableHead>
                <TableHead><strong>{t('integrations.reasoning', 'Reasoning')}</strong></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fieldMappings.map((mapping, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <span className="font-medium text-sm">
                      {mapping.externalField}
                    </span>
                  </TableCell>
                  <TableCell>→</TableCell>
                  <TableCell>
                    <Select
                      value={mapping.internalField}
                      onValueChange={(value) => handleMappingChange(mapping.externalField, value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={mapping.internalField} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={mapping.internalField}>
                          {mapping.internalField}
                        </SelectItem>
                        <SelectItem value="first_name">first_name</SelectItem>
                        <SelectItem value="last_name">last_name</SelectItem>
                        <SelectItem value="email">email</SelectItem>
                        <SelectItem value="phone_no">phone_no</SelectItem>
                        <SelectItem value="dob">dob</SelectItem>
                        <SelectItem value="gender">gender</SelectItem>
                        <SelectItem value="address_id">address_id</SelectItem>
                        <SelectItem value="city">city</SelectItem>
                        <SelectItem value="state">state</SelectItem>
                        <SelectItem value="zip">zip</SelectItem>
                        <SelectItem value="language">language</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {mapping.isManual ? (
                        <Badge variant="primary" size="sm">
                          {t('integrations.manual', 'Manual')}
                        </Badge>
                      ) : mapping.confidence === 1.0 ? (
                        <Badge variant="success" size="sm" className="gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {t('integrations.preconfigured', 'Pre-configured')}
                        </Badge>
                      ) : (
                        <Badge
                          variant={mapping.confidence >= 0.8 ? 'success' : 'warning'}
                          size="sm"
                        >
                          {Math.round(mapping.confidence * 100)}%
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {mapping.reasoning}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setActiveStep(1)}>
            {t('integrations.back_1', 'Back')}
          </Button>
          <Button variant="primary" onClick={() => setActiveStep(3)}>
            {t('integrations.review_confirm', 'Review & Confirm')}
          </Button>
        </div>
      </div>
    );
  };

  // Step 4: Confirm & Import
  const handleImport = async () => {
    if (!uploadResponse) return;

    setImporting(true);
    setError(null);

    try {
      // First, create integration config
      const configResponse = await fetch(`${getApiBaseUrl()}/api/integrations`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `Import ${uploadedFile?.name}`,
          description: 'CSV import via upload wizard',
          integration_type: 'csv_excel',
          adapter_type: 'csv_excel_member_import',
          flow_direction: 'inbound',
          connection_config: {
            file_format: 'csv',
            has_header: true
          },
          field_mappings: Object.fromEntries(
            fieldMappings.map(m => [m.externalField, m.internalField])
          )
        })
      });

      if (!configResponse.ok) {
        throw new Error('Failed to create integration config');
      }

      const config = await configResponse.json();

      // Then execute the import
      const executeResponse = await fetch(`/api/integrations/${config.id}/execute`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          execution_mode: 'manual',
          execution_params: {
            file_upload_id: uploadResponse.upload_id,
            target_table: 'members'
          }
        })
      });

      if (!executeResponse.ok) {
        throw new Error('Failed to execute import');
      }

      const result = await executeResponse.json();

      // Success!
      alert(`Import started successfully! Execution ID: ${result.execution_id}`);

      // Reset wizard
      setActiveStep(0);
      setUploadedFile(null);
      setUploadResponse(null);
      setFieldMappings([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const renderReconciliationPreview = () => {
    const runPreview = async () => {
      if (!uploadResponse?.preview) return;
      setReconLoading(true);
      try {
        const headers = uploadResponse.preview.headers;
        const rows = uploadResponse.preview.rows.map((row: any[]) => {
          const obj: Record<string, string> = {};
          headers.forEach((h: string, i: number) => {
            const mapping = fieldMappings.find(m => m.externalField === h);
            const key = mapping?.internalField || h;
            obj[key] = row[i]?.toString() || '';
          });
          return obj;
        });
        const resp = await fetch('/api/integrations/roster/reconcile-preview', {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mco_id: 1, roster_data: rows }),
        });
        if (resp.ok) {
          setReconciliationResult(await resp.json());
        } else {
          setReconciliationResult(null);
        }
      } catch {
        setReconciliationResult(null);
      } finally {
        setReconLoading(false);
      }
    };

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Reconciliation Preview</h3>
        <p className="text-sm text-gray-600">
          Compare uploaded roster against existing members to identify new, existing, re-delegated, and churned records.
        </p>

        {!reconciliationResult && (
          <Button onClick={runPreview} disabled={reconLoading} loading={reconLoading}>
            {reconLoading ? 'Analyzing...' : 'Run Reconciliation Preview'}
          </Button>
        )}

        {reconciliationResult && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'New', count: reconciliationResult.summary?.new_count || 0, color: 'bg-green-100 text-green-800', desc: 'Will be created' },
                { label: 'Existing', count: reconciliationResult.summary?.existing_count || 0, color: 'bg-blue-100 text-blue-800', desc: 'Already in system' },
                { label: 'Re-Delegated', count: reconciliationResult.summary?.re_delegated_count || 0, color: 'bg-orange-100 text-orange-800', desc: 'Different MCO' },
                { label: 'Churned', count: reconciliationResult.summary?.churned_count || 0, color: 'bg-red-100 text-red-800', desc: 'Not in roster' },
              ].map(item => (
                <div key={item.label} className={`rounded-lg p-3 ${item.color}`}>
                  <div className="text-2xl font-bold">{item.count}</div>
                  <div className="text-sm font-medium">{item.label}</div>
                  <div className="text-xs opacity-75">{item.desc}</div>
                </div>
              ))}
            </div>

            {/* Detail lists */}
            {reconciliationResult.new?.length > 0 && (
              <div>
                <h4 className="font-medium text-green-700 mb-1">New Members ({reconciliationResult.new.length})</h4>
                <div className="max-h-40 overflow-y-auto border rounded p-2 text-xs space-y-1">
                  {reconciliationResult.new.map((r: any, i: number) => (
                    <div key={i} className="flex gap-2 bg-green-50 p-1 rounded">
                      <span className="font-medium">{r.roster_data?.first_name} {r.roster_data?.last_name}</span>
                      <span className="text-gray-500">{r.roster_data?.medicaid_id}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reconciliationResult.churned?.length > 0 && (
              <div>
                <h4 className="font-medium text-red-700 mb-1">Churned Members ({reconciliationResult.churned.length})</h4>
                <div className="max-h-40 overflow-y-auto border rounded p-2 text-xs space-y-1">
                  {reconciliationResult.churned.map((r: any, i: number) => (
                    <div key={i} className="flex gap-2 bg-red-50 p-1 rounded">
                      <span className="font-medium">{r.member_name}</span>
                      <span className="text-gray-500">{r.medicaid_id}</span>
                      <span className="text-red-500 text-[10px]">{r.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex gap-2 pt-4">
          <Button variant="outline" onClick={() => setActiveStep(2)}>Back</Button>
          <Button variant="primary" onClick={() => setActiveStep(4)}>
            {reconciliationResult ? 'Continue to Import' : 'Skip Reconciliation'}
          </Button>
        </div>
      </div>
    );
  };

  const renderConfirmation = () => {
    if (!uploadResponse) return null;

    return (
      <div>
        <h2 className="text-xl font-semibold mb-4">
          {t('integrations.confirm_import', 'Confirm Import')}
        </h2>

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {t('integrations.file', 'File')}
                </p>
                <p className="text-base font-medium">
                  {uploadedFile?.name} ({((uploadedFile?.size || 0) / 1024).toFixed(2)} KB)
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {t('integrations.records', 'Records')}
                </p>
                <p className="text-base font-medium">
                  Approximately {uploadResponse.preview.total_rows_previewed} rows
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {t('integrations.field_mappings', 'Field Mappings')}
                </p>
                <p className="text-base font-medium">
                  {fieldMappings.length} fields mapped
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setActiveStep(3)} disabled={importing}>
            {t('integrations.back_2', 'Back')}
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={importing}
            loading={importing}
            icon={!importing ? <CheckCircle className="h-4 w-4" /> : undefined}
          >
            {importing ? 'Importing...' : 'Start Import'}
          </Button>
        </div>
      </div>
    );
  };

  // Custom Stepper Component
  const renderStepper = () => {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((label, index) => (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center flex-1">
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors mb-2",
                    index < activeStep
                      ? "bg-green-600 text-white"
                      : index === activeStep
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  )}
                >
                  {index < activeStep ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <span
                  className={cn(
                    "text-sm font-medium text-center",
                    index <= activeStep
                      ? "text-gray-900 dark:text-gray-100"
                      : "text-gray-500 dark:text-gray-500"
                  )}
                >
                  {label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-1 mx-2 rounded transition-colors",
                    index < activeStep
                      ? "bg-green-600"
                      : "bg-gray-200 dark:bg-gray-700"
                  )}
                  style={{ maxWidth: '100px', marginTop: '-24px' }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full p-6">
      <h1 className="text-3xl font-bold mb-6">
        {t('integrations.import_csvexcel_file', 'Import CSV/Excel File')}
      </h1>

      {renderStepper()}

      <Card>
        <CardContent className="p-6">
          {activeStep === 0 && (
            <div>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  Source System (Optional)
                </label>
                <Select
                  value={sourceSystem}
                  onValueChange={setSourceSystem}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('integrations.none_use_ai_suggestions_only', 'None - Use AI suggestions only')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">
                      {t('integrations.none_use_ai_suggestions_only', 'None - Use AI suggestions only')}
                    </SelectItem>
                    <SelectItem value="Health Net">{t('integrations.health_net', 'Health Net')}</SelectItem>
                    <SelectItem value="LA Care">{t('integrations.la_care', 'LA Care')}</SelectItem>
                    <SelectItem value="Blue Shield">{t('integrations.blue_shield', 'Blue Shield')}</SelectItem>
                    <SelectItem value="State DHCS">{t('integrations.state_dhcs', 'State DHCS')}</SelectItem>
                    <SelectItem value="CalAIM">{t('integrations.calaim', 'CalAIM')}</SelectItem>
                    <SelectItem value="FHIR">FHIR</SelectItem>
                    <SelectItem value="Other">{t('integrations.other', 'Other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Alert className="mb-4 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-900 dark:text-blue-100">
                  {sourceSystem
                    ? `Pre-configured field mappings for "${sourceSystem}" will be prioritized`
                    : 'Select a source system to use pre-configured field mappings'}
                </AlertDescription>
              </Alert>

              <div
                {...getRootProps()}
                className={cn(
                  "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all",
                  isDragActive
                    ? "border-blue-600 bg-blue-50 dark:bg-blue-900/30"
                    : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                )}
              >
                <input {...getInputProps()} />
                <Upload className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {isDragActive ? 'Drop file here' : 'Drag & drop a CSV or Excel file'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  or click to browse
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {t('integrations.supported_formats_csv_xlsx_xls', 'Supported formats: .csv, .xlsx, .xls')}
                </p>
              </div>

              {uploading && (
                <div className="mt-4">
                  <Progress value={50} className="mb-2" />
                  <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                    {t('integrations.uploading_and_analyzing_file', 'Uploading and analyzing file...')}
                  </p>
                </div>
              )}

              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {error}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {activeStep === 1 && renderDataPreview()}
          {activeStep === 2 && renderFieldMapping()}
          {activeStep === 3 && renderReconciliationPreview()}
          {activeStep === 4 && renderConfirmation()}
        </CardContent>
      </Card>
    </div>
  );
};

export default CSVUploadWizard;
